import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { object } from '../js/webGL';
import { styleHandling } from '../js/styleHandling';
import { setSelectedStyleID } from '../redux-tool/slice/toolSlice';

/**
 * Custom hook to automatically apply the first style when styles are loaded and 3D object is ready
 * @param {Array} styles - Array of style objects
 */
export function useAutoApplyFirstStyle(styles) {
  const dispatch = useDispatch();

  useEffect(() => {
    // Early return if no styles available
    if (!styles || !Array.isArray(styles) || styles.length === 0) {
      return;
    }

    // Early return if a style is already applied
    if (window.currentStyle || window.currentAppliedStyleId) {
      return;
    }

    // Function to apply first style
    const applyFirstStyle = () => {
      // Check if object is ready (try both object and window.object)
      const obj = object || window.object;
      if (!obj) {
        return false;
      }

      const firstStyle = styles[0];
      if (firstStyle && firstStyle.main_texture && styleHandling) {
        styleHandling.updateUndoStyle(firstStyle);
        styleHandling.applyStyleToAllSections(firstStyle);
        window.currentAppliedStyleId = firstStyle.Id;
        // Update selectedStyleID in Redux to keep UI in sync
        dispatch(setSelectedStyleID(String(firstStyle.Id)));
        return true;
      }
      return false;
    };

    // Try to apply immediately
    if (applyFirstStyle()) {
      return;
    }

    // If object is not ready yet, poll periodically (max 5 seconds)
    let attempts = 0;
    const maxAttempts = 50; // 50 attempts * 100ms = 5 seconds max
    const intervalId = setInterval(() => {
      attempts++;
      if (applyFirstStyle() || attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [styles, dispatch]);
}

